import type {ActionsPageHeader as ActionsPageHeaderData} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {isEnterprise} from '@github-ui/runtime-environment'
import {SoftNavOnlyLink as ReactLink} from '@github-ui/react-router'
import {useAnalytics} from '@github-ui/use-analytics'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ConfirmationDialog, IconButton, Label, Link as PrimerLink} from '@primer/react'
import {useRef, useState} from 'react'
import {StatusBadgeDialog} from './StatusBadgeDialog'
import {WorkflowDispatchMenu} from './WorkflowDispatchMenu'
import {workflowFilePathFromRoute} from '../workflow-path'

const noPinnedWorkflowIds: number[] = []

/** Props for the Actions workflow-list page header. */
export interface ActionsPageHeaderProps {
  /** Server-authored title, description, and required-workflow state. */
  header?: ActionsPageHeaderData
  /** Repository owner used to build the new-workflow URL. */
  owner: string
  /** Repository name used to build the new-workflow URL. */
  repo: string
  /** Whether the viewer may create a workflow. */
  canCreateWorkflow: boolean
  /** Whether the selected workflow has a `workflow_dispatch` trigger the viewer may run manually. */
  workflowDispatchAvailable?: boolean
  /** Whether the viewer may pin workflows. */
  allowPinning?: boolean
  /** Selected workflow filename used to load the status badge configuration. */
  workflowFilename?: string
  /** Whether the selected workflow belongs to GitHub Actions Lab. */
  statusBadgeLab?: boolean
  /** Workflow IDs currently pinned in the repository. */
  pinnedWorkflowIds?: number[]
  /** Pins the selected workflow. */
  onPinWorkflow?: (workflowId: bigint) => void
  /** Unpins the selected workflow. */
  onUnpinWorkflow?: (workflowId: bigint) => void
  /** Disables the selected workflow after confirmation. */
  onDisableWorkflow?: (workflowId: bigint) => Promise<void> | void
}

function isSafeExternalUrl(url: string) {
  try {
    const protocol = new URL(url, 'https://github.com').protocol
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

function DescriptionLink({url, children}: {url: string; children: string}) {
  // This header also supports standalone SSR without an analytics provider.
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const onClick = () =>
    sendAnalyticsEvent('analytics.click', undefined, {
      category: 'ACTIONS_WORKFLOWS',
      action: 'workflow_header.open_description',
      label: 'ref_loc:actions_workflow_header',
    })
  if (url.startsWith('/')) {
    return (
      <PrimerLink as={ReactLink} to={url} inline onClick={onClick}>
        {children}
      </PrimerLink>
    )
  }

  return isSafeExternalUrl(url) ? (
    <PrimerLink href={url} inline onClick={onClick}>
      {children}
    </PrimerLink>
  ) : null
}

interface ActionsPageHeaderActionsProps {
  owner: string
  repo: string
  canCreateWorkflow: boolean
  workflowDispatchPath?: string
  workflowFilename?: string
  statusBadgeLab?: boolean
  workflowId?: bigint
  workflowAction?: 'pin' | 'unpin'
  onPinWorkflow?: (workflowId: bigint) => void
  onUnpinWorkflow?: (workflowId: bigint) => void
  onDisableWorkflow?: (workflowId: bigint) => Promise<void> | void
}

function ActionsPageHeaderActions({
  owner,
  repo,
  canCreateWorkflow,
  workflowDispatchPath,
  workflowFilename,
  statusBadgeLab,
  workflowId,
  workflowAction,
  onPinWorkflow,
  onUnpinWorkflow,
  onDisableWorkflow,
}: ActionsPageHeaderActionsProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const inlineWorkflowMenuRef = useRef<HTMLButtonElement>(null)
  const overflowTriggerRef = useRef<HTMLButtonElement>(null)
  const [dialogReturnFocusTarget, setDialogReturnFocusTarget] = useState<'inline' | 'overflow'>('inline')
  const [isStatusBadgeOpen, setIsStatusBadgeOpen] = useState(false)
  const [isDisableWorkflowOpen, setIsDisableWorkflowOpen] = useState(false)
  const [isDisablingWorkflow, setIsDisablingWorkflow] = useState(false)
  const canCreateStatusBadge = Boolean(workflowFilename) && !isEnterprise()
  const canDisableWorkflow = workflowId !== undefined && onDisableWorkflow !== undefined
  const hasWorkflowOptions = canCreateStatusBadge || workflowAction !== undefined || canDisableWorkflow

  const openStatusBadge = (returnFocusTarget: 'inline' | 'overflow') => {
    setDialogReturnFocusTarget(returnFocusTarget)
    setIsStatusBadgeOpen(true)
    sendAnalyticsEvent('analytics.click', undefined, {
      category: 'ACTIONS_WORKFLOWS',
      action: 'status_badge.open',
      label: 'ref_cta:create_status_badge;ref_loc:actions_workflow_header',
    })
  }
  const handleWorkflowAction = () => {
    if (workflowId === undefined || workflowAction === undefined) return

    sendAnalyticsEvent('analytics.click', undefined, {
      category: 'ACTIONS_WORKFLOWS',
      action: `workflow_header.${workflowAction}_workflow`,
      label: `ref_cta:${workflowAction}_workflow;ref_loc:actions_workflow_header`,
    })
    if (workflowAction === 'pin') {
      onPinWorkflow?.(workflowId)
    } else {
      onUnpinWorkflow?.(workflowId)
    }
  }
  const openDisableWorkflow = (returnFocusTarget: 'inline' | 'overflow') => {
    setDialogReturnFocusTarget(returnFocusTarget)
    setIsDisableWorkflowOpen(true)
    sendAnalyticsEvent('analytics.click', undefined, {
      category: 'ACTIONS_WORKFLOWS',
      action: 'workflow_header.disable_workflow_open',
      label: 'ref_cta:disable_workflow;ref_loc:actions_workflow_header',
    })
  }
  const restoreWorkflowMenuFocus = () => {
    const returnFocusRef = dialogReturnFocusTarget === 'inline' ? inlineWorkflowMenuRef : overflowTriggerRef
    requestAnimationFrame(() => returnFocusRef.current?.focus())
  }
  const confirmDisableWorkflow = async () => {
    if (workflowId === undefined || !onDisableWorkflow || isDisablingWorkflow) return

    setIsDisablingWorkflow(true)
    sendAnalyticsEvent('analytics.click', undefined, {
      category: 'ACTIONS_WORKFLOWS',
      action: 'workflow_header.disable_workflow_confirm',
      label: 'ref_cta:disable_workflow;ref_loc:actions_workflow_header',
    })
    try {
      await onDisableWorkflow(workflowId)
    } catch {
      // User feedback is owned by the mutation hook.
    } finally {
      setIsDisablingWorkflow(false)
      setIsDisableWorkflowOpen(false)
      restoreWorkflowMenuFocus()
    }
  }
  const closeDisableWorkflow = () => {
    setIsDisableWorkflowOpen(false)
    sendAnalyticsEvent('analytics.click', undefined, {
      category: 'ACTIONS_WORKFLOWS',
      action: 'workflow_header.disable_workflow_cancel',
      label: 'ref_cta:cancel;ref_loc:actions_workflow_header',
    })
    restoreWorkflowMenuFocus()
  }
  const handleDisableWorkflowDialogClose = (gesture: 'confirm' | 'close-button' | 'cancel' | 'escape') => {
    if (gesture === 'confirm') {
      void confirmDisableWorkflow()
    } else {
      closeDisableWorkflow()
    }
  }
  const workflowActionLabel = workflowAction === 'unpin' ? 'Unpin workflow' : 'Pin workflow'

  return (
    <>
      {workflowDispatchPath ? (
        <WorkflowDispatchMenu
          key={`${owner}/${repo}/${workflowDispatchPath}`}
          owner={owner}
          repo={repo}
          workflowPath={workflowDispatchPath}
        />
      ) : null}
      <FilteredListPageLayout.HeaderActions
        menuAriaLabel={hasWorkflowOptions ? 'More workflow actions' : 'More actions'}
        overflowTriggerRef={overflowTriggerRef}
        actions={[
          ...(canCreateWorkflow
            ? [
                {
                  key: 'new-workflow',
                  label: 'New workflow',
                  primary: false,
                  href: `/${owner}/${repo}/actions/new`,
                  onClick: () =>
                    sendAnalyticsEvent('analytics.click', undefined, {
                      category: 'ACTIONS_WORKFLOWS',
                      action: 'workflow_header.new_workflow',
                      label: 'ref_cta:new_workflow;ref_loc:actions_workflow_header',
                    }),
                },
              ]
            : []),
          ...(hasWorkflowOptions
            ? [
                {
                  key: 'workflow-options',
                  renderInline: () => (
                    <ActionMenu
                      anchorRef={inlineWorkflowMenuRef}
                      onOpenChange={open => {
                        if (open) {
                          sendAnalyticsEvent('analytics.click', undefined, {
                            category: 'ACTIONS_WORKFLOWS',
                            action: 'workflow_header.open_options',
                            label: 'ref_loc:actions_workflow_header',
                          })
                        }
                      }}
                    >
                      <ActionMenu.Anchor>
                        <IconButton aria-label="Workflow options" icon={KebabHorizontalIcon} />
                      </ActionMenu.Anchor>
                      <ActionMenu.Overlay align="end">
                        <ActionList>
                          {canCreateStatusBadge ? (
                            <ActionList.Item onSelect={() => openStatusBadge('inline')}>
                              Create status badge
                            </ActionList.Item>
                          ) : null}
                          {workflowAction ? (
                            <ActionList.Item onSelect={handleWorkflowAction}>{workflowActionLabel}</ActionList.Item>
                          ) : null}
                          {canDisableWorkflow ? (
                            <ActionList.Item variant="danger" onSelect={() => openDisableWorkflow('inline')}>
                              Disable workflow
                            </ActionList.Item>
                          ) : null}
                        </ActionList>
                      </ActionMenu.Overlay>
                    </ActionMenu>
                  ),
                  renderMenuItem: () => (
                    <>
                      {canCreateStatusBadge ? (
                        <ActionList.Item onSelect={() => openStatusBadge('overflow')}>
                          Create status badge
                        </ActionList.Item>
                      ) : null}
                      {workflowAction ? (
                        <ActionList.Item onSelect={handleWorkflowAction}>{workflowActionLabel}</ActionList.Item>
                      ) : null}
                      {canDisableWorkflow ? (
                        <ActionList.Item variant="danger" onSelect={() => openDisableWorkflow('overflow')}>
                          Disable workflow
                        </ActionList.Item>
                      ) : null}
                    </>
                  ),
                },
              ]
            : []),
        ]}
      />
      {canCreateStatusBadge && workflowFilename ? (
        <StatusBadgeDialog
          isOpen={isStatusBadgeOpen}
          lab={statusBadgeLab}
          onClose={() => setIsStatusBadgeOpen(false)}
          owner={owner}
          repo={repo}
          returnFocusRef={dialogReturnFocusTarget === 'inline' ? inlineWorkflowMenuRef : overflowTriggerRef}
          workflowFilename={workflowFilename}
        />
      ) : null}
      {isDisableWorkflowOpen ? (
        <ConfirmationDialog
          title="Disable workflow?"
          confirmButtonContent="Disable workflow"
          confirmButtonType="danger"
          confirmButtonLoading={isDisablingWorkflow}
          cancelButtonLoading={isDisablingWorkflow}
          onClose={handleDisableWorkflowDialogClose}
        >
          Disabling this workflow prevents future runs until it is enabled again.
        </ConfirmationDialog>
      ) : null}
    </>
  )
}

/** Renders the dynamic workflow-list title, description, and creation action. */
export function ActionsPageHeader({
  header,
  owner,
  repo,
  canCreateWorkflow,
  workflowDispatchAvailable = false,
  allowPinning = false,
  workflowFilename,
  statusBadgeLab,
  pinnedWorkflowIds = noPinnedWorkflowIds,
  onPinWorkflow,
  onUnpinWorkflow,
  onDisableWorkflow,
}: ActionsPageHeaderProps) {
  const title = header?.title || 'Workflow runs'
  const descriptionText = header?.descriptionText ?? ''
  const descriptionLinkText = header?.descriptionLinkText ?? ''
  const descriptionLinkUrl = header?.descriptionLinkUrl ?? ''
  const hasDescription = descriptionText.length > 0 || (descriptionLinkText.length > 0 && descriptionLinkUrl.length > 0)
  const workflowId = header?.workflowId
  const workflowIsPinned = pinnedWorkflowIds.some(
    pinnedWorkflowId => pinnedWorkflowId.toString() === workflowId?.toString(),
  )
  const canManageWorkflow = allowPinning && header?.workflowActive === true && workflowId !== undefined
  let workflowAction: 'pin' | 'unpin' | undefined
  if (!canManageWorkflow) {
    workflowAction = undefined
  } else if (workflowIsPinned && onUnpinWorkflow) {
    workflowAction = 'unpin'
  } else if (!workflowIsPinned && onPinWorkflow) {
    workflowAction = 'pin'
  } else {
    workflowAction = undefined
  }
  const hasStatusBadgeAction = Boolean(workflowFilename) && !isEnterprise()
  const canDisableWorkflow = canManageWorkflow && onDisableWorkflow !== undefined
  const workflowDispatchPath =
    workflowDispatchAvailable && workflowFilename
      ? workflowFilePathFromRoute(workflowFilename, {lab: statusBadgeLab})
      : undefined
  const hasActions =
    canCreateWorkflow ||
    workflowDispatchPath !== undefined ||
    workflowAction !== undefined ||
    hasStatusBadgeAction ||
    canDisableWorkflow

  return (
    <FilteredListPageLayout.PageHeader>
      <FilteredListPageLayout.PageHeader.TitleArea className="min-width-0">
        <FilteredListPageLayout.PageHeader.Title as="h1">{title}</FilteredListPageLayout.PageHeader.Title>
        {header?.requiredByRuleset ? (
          <FilteredListPageLayout.PageHeader.TrailingVisual>
            <Label variant="secondary">Required by ruleset</Label>
          </FilteredListPageLayout.PageHeader.TrailingVisual>
        ) : null}
      </FilteredListPageLayout.PageHeader.TitleArea>
      {hasDescription ? (
        <FilteredListPageLayout.PageHeader.Description>
          {descriptionText ? <span>{descriptionText}</span> : null}
          {descriptionLinkText && descriptionLinkUrl ? (
            <DescriptionLink url={descriptionLinkUrl}>{descriptionLinkText}</DescriptionLink>
          ) : null}
        </FilteredListPageLayout.PageHeader.Description>
      ) : null}
      {hasActions ? (
        <FilteredListPageLayout.PageHeader.Actions>
          <ActionsPageHeaderActions
            key={workflowFilename ?? 'all-workflows'}
            owner={owner}
            repo={repo}
            canCreateWorkflow={canCreateWorkflow}
            workflowDispatchPath={workflowDispatchPath}
            workflowFilename={workflowFilename}
            statusBadgeLab={statusBadgeLab}
            workflowId={workflowId}
            workflowAction={workflowAction}
            onPinWorkflow={onPinWorkflow}
            onUnpinWorkflow={onUnpinWorkflow}
            onDisableWorkflow={canDisableWorkflow ? onDisableWorkflow : undefined}
          />
        </FilteredListPageLayout.PageHeader.Actions>
      ) : null}
      {/* TODO(M1.1d): Confirm the template sidebar trigger fully replaces the classic mobile workflow menu. */}
    </FilteredListPageLayout.PageHeader>
  )
}
